import React, { useEffect, useRef, useState } from "react";
import "./RentalStats.css";

const BuildingIcon = () => (
  <svg viewBox="0 0 24 24" className="rentalstats-icon" aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10H3M21 14H3M5 10V21M19 10V21M9 10V21M15 10V21M10 3h4v7h-4z"/>
      <rect x="3" y="3" width="18" height="18" rx="2"/>
    </g>
  </svg>
);

const StarIcon = () => (
  <svg viewBox="0 0 24 24" className="rentalstats-icon" aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    </g>
  </svg>
);

const HistoryIcon = () => (
  <svg viewBox="0 0 24 24" className="rentalstats-icon" aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 6v6l4 2"/>
    </g>
  </svg>
);

const HomeIcon = () => (
  <svg viewBox="0 0 24 24" className="rentalstats-icon" aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 11.5l9-7 9 7" />
      <path d="M5.5 10.5V20a1 1 0 0 0 1 1H17a1 1 0 0 0 1-1v-9.5" />
      <path d="M10 21v-6h4v6" />
    </g>
  </svg>
);

function CountUp({ to = 0, start = false, duration = 1200 }) {
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!start) return;
    let raf = 0;
    let t0 = 0;
    const step = (t) => {
      if (!t0) t0 = t;
      const p = Math.min((t - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(to * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, start, duration]);

  return <>{val.toLocaleString()}</>;
}

const STATS = [
  { label: "Loft Apartments", value: 56, icon: HomeIcon },
  { label: "Commercial Floors", value: 3, suffix: "", icon: BuildingIcon },
  { label: "Years of History", value: 100, suffix: "+", icon: HistoryIcon },
  { label: "Resident Rating", value: 4.8, suffix: "/5", icon: StarIcon },
];

export default function RentalStats({
  heading = "The Historic Harmon by the Numbers",
  eyebrow = "Landmark Living",
  blurb = "Experience modern living in Tacoma's most iconic 9-story building with over a century of history.",
  stats = STATS,
}) {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="rentalstats" ref={sectionRef}>
      <div className="rentalstats-container">
        <header className="rentalstats-header">
          <div className="rentalstats-heading-wrapper">
            <span className="rentalstats-eyebrow">{eyebrow}</span>
            <h2 className="rentalstats-title">{heading}</h2>
            <div className="rentalstats-underline" aria-hidden="true"></div>
          </div>
          <p className="rentalstats-blurb">{blurb}</p>
        </header>

        <div className="rentalstats-grid">
          {stats.map(({ label, value, suffix = "", icon: Icon }, index) => (
            <div className="rentalstats-card" key={index}>
              <div className="rentalstats-card-inner">
                <div className="rentalstats-icon-container">
                  <div className="rentalstats-icon-backdrop" />
                  <Icon />
                </div>
                <div className="rentalstats-number">
                  <CountUp to={value} start={isVisible} />
                  {suffix && <span className="rentalstats-suffix">{suffix}</span>}
                </div>
                <h3 className="rentalstats-label">{label}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}